import { createFileRoute, Link, useNavigate, notFound } from "@tanstack/react-router";
import { useRef } from "react";
import { concepts } from "@/components/koncepcje";

type Device = "desktop" | "mobile";

export const Route = createFileRoute("/koncepcje/$id")({
  validateSearch: (search: Record<string, unknown>): { device: Device } => ({
    device: search["device"] === "mobile" ? "mobile" : "desktop",
  }),
  loader: ({ params }) => {
    const concept = concepts.find((c) => c.id === params.id);
    if (!concept) throw notFound();
    return { name: concept.name };
  },
  head: ({ loaderData }) => {
    const title = `Koncepcja ${loaderData?.name ?? ""}, podgląd pełnoekranowy`;
    const description = `Pełnoekranowy podgląd koncepcji wizualnej ${loaderData?.name ?? ""} strony Sylwii Sitkowskiej.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "noindex, nofollow" },
      ],
    };
  },
  component: ConceptFullscreen,
});

function ConceptFullscreen() {
  const { id } = Route.useParams();
  const { device } = Route.useSearch();
  const navigate = useNavigate({ from: "/koncepcje/$id" });
  const stageRef = useRef<HTMLDivElement>(null);

  const concept = concepts.find((c) => c.id === id)!;
  const Preview = concept.Preview;

  const goFullscreen = () => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen?.();
  };

  return (
    <div className="min-h-screen bg-ink">
      <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-3">
        <div className="flex items-center gap-4">
          <Link to="/koncepcje" className="text-xs uppercase tracking-[0.14em] text-mist/70 hover:text-mist">
            ← Wszystkie koncepcje
          </Link>
          <span className="text-sm font-medium text-mist">{concept.name}</span>
        </div>

        <div className="flex items-center gap-2">
          {(["desktop", "mobile"] as Device[]).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => void navigate({ search: { device: d }, params: { id } })}
              className={`px-4 py-2 text-xs uppercase tracking-[0.14em] transition-colors ${
                device === d ? "bg-coral text-mist" : "border border-mist/30 text-mist/70 hover:text-mist"
              }`}
            >
              {d === "desktop" ? "Desktop" : "Mobile"}
            </button>
          ))}
          <button
            type="button"
            onClick={goFullscreen}
            className="bg-mist px-4 py-2 text-xs uppercase tracking-[0.14em] text-ink"
          >
            Pełny ekran
          </button>
        </div>
      </div>

      <div className="flex justify-center px-4 pb-10">
        <div
          ref={stageRef}
          className="w-full overflow-auto bg-mist"
          style={{ maxWidth: device === "mobile" ? "24rem" : "100%", minHeight: "80vh" }}
        >
          <Preview device={device} />
        </div>
      </div>
    </div>
  );
}
